"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import { cfFetch } from "../api/apiClient";

export type ForecastData = Record<string, number>; // key: `${month}:${rowKey}`
type PendingUpdate = { month: string; rowKey: string; value: number | null };

const SAVE_DEBOUNCE_MS = 450;

export function useForecast() {
  const [data, setData] = useState<ForecastData>({});
  const [isLoading, setIsLoading] = useState(true);
  const pendingRef = useRef<Map<string, PendingUpdate>>(new Map());
  const timerRef = useRef<number | null>(null);

  const flushPending = useCallback(async () => {
    const updates = Array.from(pendingRef.current.values());
    if (!updates.length) return;
    pendingRef.current.clear();
    try {
      await cfFetch("/cashflow/forecast", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ values: updates }),
      });
    } catch {
      // Silent fail to keep table editing uninterrupted.
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setIsLoading(true);
      try {
        const res = await cfFetch("/cashflow/forecast");
        const json = await res.json();
        if (!res.ok || !mounted) return;
        const next: ForecastData = {};
        for (const item of json.values ?? []) {
          if (
            typeof item?.month === "string" &&
            typeof item?.rowKey === "string"
          ) {
            const num = Number(item.value);
            if (!Number.isNaN(num)) next[`${item.month}:${item.rowKey}`] = num;
          }
        }
        setData(next);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }
    void load();
    return () => {
      mounted = false;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      void flushPending();
    };
  }, [flushPending]);

  const setValue = useCallback(
    (month: string, rowKey: string, value: number | null) => {
      setData((prev) => {
        const next = { ...prev };
        const key = `${month}:${rowKey}`;
        if (value === null) {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });

      const k = `${month}:${rowKey}`;
      pendingRef.current.set(k, { month, rowKey, value });
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        void flushPending();
      }, SAVE_DEBOUNCE_MS);
    },
    [flushPending],
  );

  const getValue = useCallback(
    (month: string, rowKey: string): number | null => {
      return data[`${month}:${rowKey}`] ?? null;
    },
    [data],
  );

  return { getValue, setValue, isLoading };
}
